import type { SolutionPageContent } from '../../types/solution'

export const manufacturingSolution: SolutionPageContent = {
  slug: 'manufacturing',
  hero: {
    headline: 'Know your materials, production, and real costs',
    subheadline: 'Track raw materials, finished goods, and orders so you always know what it costs to make and sell.',
    card: {
      type: 'progress',
      title: 'Raw materials · Gikondo',
      badge: { label: '1 low', tone: 'warning' },
      rows: [
        { label: 'Eucalyptus planks', value: '42 pcs', pct: 22, tone: 'warning' },
        { label: 'Wood glue (L)', value: '18 L', pct: 60 },
        { label: 'Varnish (L)', value: '26 L', pct: 78 },
      ],
      action: 'Order materials',
    },
  },
  challenges: [
    {
      quote: 'We start a job and then find we’re short on materials.',
      detail: 'Production stops while we wait, and delivery dates slip.',
    },
    {
      quote: 'I don’t know what each product really costs to make.',
      detail: 'Material prices change every month, but our prices don’t.',
    },
    {
      quote: 'Customers owe us for orders and I lose track.',
      detail: 'Deposits, balances, and deliveries are spread across notebooks.',
    },
  ],
  helps: [
    {
      solves: 'We’re short on materials',
      title: 'Raw materials and finished goods in one place',
      copy: 'Track materials as they come in and go into production, and finished goods as they’re sold. Low-stock alerts warn you before a job stalls.',
      product: 'inventory',
      visual: {
        kind: 'card',
        card: {
          type: 'list',
          title: 'Finished goods',
          rows: [
            { label: 'Dining chair', sublabel: 'Ready to sell', value: '24 pcs' },
            { label: 'Office desk', sublabel: 'In production', value: '6 pcs', tone: 'brand' },
            { label: 'Bed frame (double)', sublabel: 'Ready to sell', value: '3 pcs', tone: 'warning' },
          ],
        },
      },
    },
    {
      solves: 'I don’t know what each product costs',
      title: 'Supplier costs that feed your margins',
      copy: 'Record what you pay for every material. Kayko uses real purchase prices to show the cost and margin of what you make.',
      product: 'purchases',
      visual: {
        kind: 'card',
        card: {
          type: 'list',
          title: 'Purchase history · Planks',
          rows: [
            { label: 'Nyabugogo Timber', sublabel: '12 Sep · 80 pcs', value: 'RWF 6,500/pc' },
            { label: 'Nyabugogo Timber', sublabel: '14 Aug · 60 pcs', value: 'RWF 6,000/pc' },
            { label: 'Musanze Wood Co.', sublabel: '2 Jul · 50 pcs', value: 'RWF 5,800/pc' },
          ],
          footer: { label: 'Price change (3 months)', value: '+12%' },
        },
      },
    },
    {
      solves: 'Customers owe us for orders',
      title: 'Deposits and balances for every order',
      copy: 'Record deposits, track what each customer still owes, and send a reminder by SMS when a balance is due.',
      product: 'customers',
      visual: {
        kind: 'card',
        card: {
          type: 'list',
          title: 'Customer balances',
          rows: [
            { label: 'Hotel Umubano', sublabel: '20 chairs · delivered', value: 'RWF 480,000', tone: 'warning' },
            { label: 'Kigali Heights Office', sublabel: '6 desks · in production', value: 'RWF 900,000' },
            { label: 'Aline Mukamana', sublabel: 'Bed frame · paid', value: 'Settled', tone: 'positive' },
          ],
          footer: { label: 'Total owed', value: 'RWF 1,380,000' },
        },
      },
    },
  ],
  journey: {
    heading: 'A day with Kayko',
    subheading: 'From the materials store to the delivery van.',
    items: [
      { time: '7:00', title: 'Check materials', copy: 'See what’s in stock for today’s jobs.' },
      { time: '9:00', title: 'Receive a delivery', copy: 'Record new materials and what you paid.' },
      { time: '13:00', title: 'Finish a batch', copy: 'Move finished goods into stock.' },
      { time: '16:00', title: 'Deliver and invoice', copy: 'Send an EBM invoice and record the payment.' },
    ],
  },
  results: [
    { value: '−40%', label: 'fewer production stops from missing materials' },
    { value: '100%', label: 'of products with a known cost to make' },
    { value: '2×', label: 'faster collection of customer balances' },
  ],
  testimonial: {
    quote: 'I used to guess my costs. Now I price every order knowing exactly what the wood and varnish cost me.',
    name: 'Eric Habimana',
    role: 'Founder',
    business: 'Habimana Furniture Works',
    location: 'Gikondo, Kigali',
    image: 'https://cdn.magicpatterns.com/patterns/generated-images/66b3008d-3a38-45cd-9a5b-45b7d3e19ed6.jpg',
    metric: { value: '+18%', label: 'higher margin after repricing' },
  },
  products: ['inventory', 'purchases', 'customers', 'reports'],
  plan: { name: 'Growth', audience: 'manufacturers' },
  faq: [
    {
      question: 'Can I track raw materials and finished goods separately?',
      answer: 'Yes. Keep separate stock for materials and finished products, each with its own reorder levels.',
    },
    {
      question: 'Can I record customer deposits?',
      answer: 'Yes. Record a deposit against an order, and Kayko tracks the remaining balance until it’s paid.',
    },
    {
      question: 'Can I issue EBM invoices for large orders?',
      answer: 'Yes. Create EBM-compliant invoices for business customers and send them by email or SMS.',
    },
    {
      question: 'Does Kayko handle full production planning?',
      answer: 'Kayko tracks materials, finished goods, and costs. Detailed production scheduling isn’t included today.',
    },
    {
      question: 'Can I see costs across several suppliers?',
      answer: 'Yes. Purchase history shows what each supplier charged over time, so you can compare prices.',
    },
  ],
  related: ['retail', 'manage-inventory', 'control-expenses'],
  cta: {
    headline: 'Know what every product costs to make',
    subheadline: 'Set up in minutes. Local support in Kigali when you need it.',
  },
}
